/**
 * FikoCastingCost - Dokumhane Maliyet Hesaplama Modulu
 *
 * Kum dokum, basincli dokum, hassas dokum, kabuk kalip ve savurma
 * dokum surecleri icin detayli maliyet hesaplama.
 *
 * Hesaplama temeli: kg-bazli fiyatlandirma + surec maliyetleri
 */

import { FikoMaterialDB, MaterialProperties, ManufacturingProcessType } from './FikoMaterialDB';
import { CostLineItem } from './FikoCostEngine';

// ============================================================
// CASTING COST TYPES
// ============================================================

export type CastingProcessType = 'sand_casting' | 'die_casting' | 'investment_casting' | 'shell_mold_casting' | 'centrifugal_casting';

export interface CastingCostInput {
    partGeometry: {
        volumeCm3: number;
        surfaceAreaCm2: number;
        boundingBox: { x: number; y: number; z: number };
        complexityScore: number;
        holeCount: number;
        undercutCount: number;
    };
    material: MaterialProperties;
    batchSize: number;
    annualVolume: number;
    castingProcess: CastingProcessType;
    complexityFactor: number;       // 0.0-1.0 (low to high)
    requiresMachining: boolean;
    machiningAllowanceMM: number;
    coreCount?: number;             // Cekirek sayisi
    patternLifeExpected?: number;   // Model omru (kac dokum)
}

export interface CastingCostResult {
    costPerPart: number;
    costPerKg: number;
    breakdown: CostLineItem[];
    netWeightKg: number;
    grossWeightKg: number;      // yolluk + besleme dahil
    yieldPercent: number;       // Verim orani
    notes: string[];
    notesTR: string[];
}

// ============================================================
// CASTING COST CALCULATOR
// ============================================================

export class FikoCastingCost {
    private materialDB: FikoMaterialDB;

    constructor(materialDB: FikoMaterialDB) {
        this.materialDB = materialDB;
    }

    calculate(input: CastingCostInput): CastingCostResult {
        const { material, partGeometry, batchSize, annualVolume, castingProcess } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        // 1. Agirlik hesaplama
        const netWeightKg = partGeometry.volumeCm3 * material.density;
        const yieldPercent = this.getYieldPercent(castingProcess, input.complexityFactor);
        const grossWeightKg = netWeightKg / (yieldPercent / 100);

        // 2. Malzeme maliyeti (kg bazli)
        const materialPricePerKg = material.castingPricePerKg || material.pricePerKg;
        const materialCost = grossWeightKg * materialPricePerKg;
        breakdown.push({
            category: 'material',
            description: `${material.name} (gross ${grossWeightKg.toFixed(2)}kg at $${materialPricePerKg.toFixed(2)}/kg)`,
            descriptionTR: `${material.nameTR} (brut ${grossWeightKg.toFixed(2)}kg, $${materialPricePerKg.toFixed(2)}/kg)`,
            quantity: grossWeightKg,
            unitCost: materialPricePerKg,
            totalCost: materialCost,
            unit: 'kg'
        });

        // 3. Eritme maliyeti
        const meltingCostPerKg = this.getMeltingCostPerKg(castingProcess, material);
        const meltingCost = grossWeightKg * meltingCostPerKg;
        breakdown.push({
            category: 'machine',
            description: `Melting cost ($${meltingCostPerKg.toFixed(2)}/kg)`,
            descriptionTR: `Eritme maliyeti ($${meltingCostPerKg.toFixed(2)}/kg)`,
            quantity: grossWeightKg,
            unitCost: meltingCostPerKg,
            totalCost: meltingCost,
            unit: 'kg'
        });

        // 4. Kalip/Model maliyeti (amortize)
        const patternCost = this.estimatePatternCost(castingProcess, partGeometry, input.complexityFactor);
        const patternLife = input.patternLifeExpected || this.getPatternLife(castingProcess);
        const amortizedPatternCost = patternCost / Math.min(annualVolume, patternLife);
        breakdown.push({
            category: 'tooling',
            description: `Pattern/mold cost ($${patternCost.toFixed(0)} amortized over ${Math.min(annualVolume, patternLife)} parts)`,
            descriptionTR: `Model/kalip maliyeti ($${patternCost.toFixed(0)}, ${Math.min(annualVolume, patternLife)} parcaya amorti)`,
            quantity: 1,
            unitCost: amortizedPatternCost,
            totalCost: amortizedPatternCost,
            unit: 'adet'
        });

        // 5. Cekirek maliyeti
        const coreCount = input.coreCount || this.estimateCoreCount(partGeometry);
        if (coreCount > 0 && (castingProcess === 'sand_casting' || castingProcess === 'shell_mold_casting')) {
            const coreCostEach = this.getCoreCost(castingProcess, partGeometry);
            const totalCoreCost = coreCount * coreCostEach;
            breakdown.push({
                category: 'material',
                description: `${coreCount} sand core(s) at $${coreCostEach.toFixed(2)} each`,
                descriptionTR: `${coreCount} kum cekirdegi, tanesi $${coreCostEach.toFixed(2)}`,
                quantity: coreCount,
                unitCost: coreCostEach,
                totalCost: totalCoreCost,
                unit: 'adet'
            });
            notes.push(`${coreCount} core(s) required for internal cavities`);
            notesTR.push(`Ic bosluklar icin ${coreCount} cekirek gerekli`);
        }

        // 6. Kum/kalip malzeme maliyeti (kum dokum icin)
        if (castingProcess === 'sand_casting' || castingProcess === 'shell_mold_casting') {
            const moldVolume = this.estimateMoldVolume(partGeometry);
            const sandCostPerLiter = castingProcess === 'sand_casting' ? 0.05 : 0.15;
            const sandRecycleRate = castingProcess === 'sand_casting' ? 0.85 : 0.5;
            const sandCost = moldVolume * sandCostPerLiter * (1 - sandRecycleRate);
            breakdown.push({
                category: 'material',
                description: `Sand/mold material (${moldVolume.toFixed(1)}L, ${(sandRecycleRate * 100).toFixed(0)}% recycled)`,
                descriptionTR: `Kum/kalip malzemesi (${moldVolume.toFixed(1)}L, %${(sandRecycleRate * 100).toFixed(0)} geri donusum)`,
                quantity: moldVolume,
                unitCost: sandCostPerLiter * (1 - sandRecycleRate),
                totalCost: sandCost,
                unit: 'litre'
            });
        }

        // 7. Iscilik maliyeti
        const laborMinutes = this.estimateLaborMinutes(castingProcess, grossWeightKg, input.complexityFactor);
        const laborRate = 25; // $/saat
        const laborCost = (laborMinutes / 60) * laborRate;
        breakdown.push({
            category: 'labor',
            description: `Foundry labor (${laborMinutes.toFixed(0)} min at $${laborRate}/hr)`,
            descriptionTR: `Dokumhane isciligi (${laborMinutes.toFixed(0)} dk, $${laborRate}/saat)`,
            quantity: laborMinutes / 60,
            unitCost: laborRate,
            totalCost: laborCost,
            unit: 'saat'
        });

        // 8. Temizleme / capak alma / taslama
        const finishingCost = this.estimateFinishingCost(castingProcess, grossWeightKg, partGeometry.surfaceAreaCm2);
        breakdown.push({
            category: 'post_processing',
            description: 'Fettling, grinding, cleaning',
            descriptionTR: 'Temizleme, taslama, capak alma',
            quantity: 1,
            unitCost: finishingCost,
            totalCost: finishingCost,
            unit: 'adet'
        });

        // 9. Isleme payi (gerekiyorsa)
        if (input.requiresMachining) {
            const machiningCost = this.estimatePostCastMachining(partGeometry, material, input.machiningAllowanceMM);
            breakdown.push({
                category: 'post_processing',
                description: `Post-cast machining (${input.machiningAllowanceMM}mm allowance)`,
                descriptionTR: `Dokum sonrasi isleme (${input.machiningAllowanceMM}mm isleme payi)`,
                quantity: 1,
                unitCost: machiningCost,
                totalCost: machiningCost,
                unit: 'adet'
            });
        }

        // 10. Hazirlik/Setup maliyeti (parti basina amorti)
        const machineParams = this.materialDB.getMachineParams(castingProcess as ManufacturingProcessType);
        const setupCost = machineParams ? (machineParams.setupTimeMinutes / 60) * machineParams.hourlyRate / batchSize : 0;
        if (setupCost > 0.01) {
            breakdown.push({
                category: 'setup',
                description: `Setup cost (amortized over ${batchSize} parts)`,
                descriptionTR: `Hazirlik maliyeti (${batchSize} parcaya amorti)`,
                quantity: 1,
                unitCost: setupCost,
                totalCost: setupCost,
                unit: 'adet'
            });
        }

        // Toplam
        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        const costPerKg = netWeightKg > 0 ? costPerPart / netWeightKg : 0;

        // Notlar
        notes.push(`Yield: ${yieldPercent.toFixed(0)}% (net ${netWeightKg.toFixed(2)}kg / gross ${grossWeightKg.toFixed(2)}kg)`);
        notes.push(`Shrinkage allowance: ${(material.shrinkageAllowance * 100).toFixed(1)}%`);
        notesTR.push(`Verim: %${yieldPercent.toFixed(0)} (net ${netWeightKg.toFixed(2)}kg / brut ${grossWeightKg.toFixed(2)}kg)`);
        notesTR.push(`Cekme payi: %${(material.shrinkageAllowance * 100).toFixed(1)}`);

        if (castingProcess === 'die_casting' && batchSize < 500) {
            notes.push('WARNING: Die casting typically requires 500+ parts to justify tooling');
            notesTR.push('UYARI: Basincli dokum tipik olarak kalip maliyetini karsilamak icin 500+ parca gerektirir');
        }

        return {
            costPerPart: Math.round(costPerPart * 100) / 100,
            costPerKg: Math.round(costPerKg * 100) / 100,
            breakdown,
            netWeightKg,
            grossWeightKg,
            yieldPercent,
            notes,
            notesTR
        };
    }

    // ============================================================
    // HELPER METHODS
    // ============================================================

    private getYieldPercent(process: CastingProcessType, complexity: number): number {
        const baseYields: Record<CastingProcessType, number> = {
            sand_casting: 65,
            die_casting: 85,
            investment_casting: 80,
            shell_mold_casting: 70,
            centrifugal_casting: 75,
        };
        // Complexity reduces yield
        return (baseYields[process] || 70) * (1 - complexity * 0.15);
    }

    private getMeltingCostPerKg(process: CastingProcessType, material: MaterialProperties): number {
        // Base melting cost varies by process and material melting point
        const baseCost = material.meltingPoint > 1400 ? 1.2 : material.meltingPoint > 1000 ? 0.8 : 0.5;
        const processMultiplier: Record<CastingProcessType, number> = {
            sand_casting: 1.0,
            die_casting: 0.8,      // More efficient furnaces
            investment_casting: 1.3, // Vacuum melting for some
            shell_mold_casting: 1.0,
            centrifugal_casting: 0.9,
        };
        return baseCost * (processMultiplier[process] || 1.0);
    }

    private estimatePatternCost(process: CastingProcessType, geom: any, complexity: number): number {
        const volume = geom.boundingBox.x * geom.boundingBox.y * geom.boundingBox.z; // mm3
        const sizeFactor = Math.pow(volume / 1e6, 0.4); // Normalize to ~1 for 100mm cube

        const baseCosts: Record<CastingProcessType, number> = {
            sand_casting: 800,          // Wood/resin pattern
            die_casting: 15000,         // Steel die
            investment_casting: 2000,   // Wax injection mold
            shell_mold_casting: 3000,   // Metal pattern
            centrifugal_casting: 1500,  // Mold
        };

        const base = baseCosts[process] || 2000;
        const complexityMultiplier = 1 + complexity * 2; // 1x to 3x
        return base * sizeFactor * complexityMultiplier;
    }

    private getPatternLife(process: CastingProcessType): number {
        const lives: Record<CastingProcessType, number> = {
            sand_casting: 5000,        // Wood: 5000, Metal: 50000
            die_casting: 100000,       // Steel die
            investment_casting: 20000, // Wax mold
            shell_mold_casting: 30000,
            centrifugal_casting: 10000,
        };
        return lives[process] || 10000;
    }

    private estimateCoreCount(geom: any): number {
        // Estimate from hole count and undercuts
        return Math.ceil((geom.holeCount || 0) * 0.3 + (geom.undercutCount || 0) * 0.5);
    }

    private getCoreCost(process: CastingProcessType, geom: any): number {
        const bbVol = geom.boundingBox.x * geom.boundingBox.y * geom.boundingBox.z;
        const sizeFactor = Math.pow(bbVol / 1e6, 0.3);
        return process === 'shell_mold_casting' ? 3.0 * sizeFactor : 1.5 * sizeFactor;
    }

    private estimateMoldVolume(geom: any): number {
        // Mold box is typically 2x bounding box
        const bbVolMM3 = geom.boundingBox.x * geom.boundingBox.y * geom.boundingBox.z;
        const moldVolMM3 = bbVolMM3 * 2.5;
        return moldVolMM3 / 1e6; // Convert to liters
    }

    private estimateLaborMinutes(process: CastingProcessType, grossWeightKg: number, complexity: number): number {
        const baseMinutes: Record<CastingProcessType, number> = {
            sand_casting: 30,
            die_casting: 5,
            investment_casting: 45,
            shell_mold_casting: 25,
            centrifugal_casting: 20,
        };
        const base = baseMinutes[process] || 20;
        const weightFactor = 1 + Math.log10(Math.max(1, grossWeightKg)) * 0.5;
        const complexityFactor = 1 + complexity * 1.5;
        return base * weightFactor * complexityFactor;
    }

    private estimateFinishingCost(process: CastingProcessType, grossWeightKg: number, surfaceAreaCm2: number): number {
        // Base: $0.5-2/kg for finishing
        const ratePerKg: Record<CastingProcessType, number> = {
            sand_casting: 1.5,
            die_casting: 0.5,
            investment_casting: 0.8,
            shell_mold_casting: 1.2,
            centrifugal_casting: 1.0,
        };
        return grossWeightKg * (ratePerKg[process] || 1.0);
    }

    private estimatePostCastMachining(geom: any, material: MaterialProperties, allowanceMM: number): number {
        // Rough estimate based on surface area needing machining (assume 30% of total)
        const machinedAreaCm2 = geom.surfaceAreaCm2 * 0.3;
        // Machine removal rate ~ 20 cm3/min for cast iron, slower for others
        const removalRate = 20 * (material.machinabilityIndex / 100);
        const removalVolumeCm3 = machinedAreaCm2 * (allowanceMM / 10);
        const machineTimeMinutes = removalVolumeCm3 / Math.max(1, removalRate);
        const hourlyRate = 65; // $/hr for 3-axis CNC
        return (machineTimeMinutes / 60) * hourlyRate;
    }
}
